import classNames from "classnames"
import type { ReactNode } from "react"
import { Fragment, useState, useRef, useEffect } from "react"
import Link from "~/components/Link"
import ChevronRight from "~/icons/ChevronRight"

export type Item = {
  title: string
  imageUrl: string
  imageWidth: number | undefined
  imageHeight: number | undefined
  url: string
  region?: string
}

interface Props {
  items: Item[] | ReactNode[]
  className?: string
  square?: boolean
  onClick?: () => void
  hideArrow?: boolean
}

const HorizontalDataList = ({
  items,
  square,
  className,
  onClick,
  hideArrow
}: Props) => {
  const [hasScrolled, setHasScrolled] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const checkOverflow = () => {
      setHasOverflow(container.scrollWidth > container.clientWidth)
    }

    // Check initially and on window resize
    checkOverflow()
    window.addEventListener("resize", checkOverflow)

    const handleScroll = () => {
      if (container.scrollLeft > 0 && !hasScrolled) {
        setHasScrolled(true)
      }
    }

    container.addEventListener("scroll", handleScroll)
    return () => {
      container.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", checkOverflow)
    }
  }, [hasScrolled])

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className={classNames(
          "flex overflow-x-auto rounded-scrollbars scrollbar-thin scrollbar-track-cream-50 scrollbar-thumb-camp-primary pb-4",
          className,
          {
            "space-x-4": square,
            "space-x-5": !square
          }
        )}
      >
        {items.map((item, index) =>
          item && typeof item === "object" && "title" in item ? (
            <ListItem
              item={item as Item}
              key={index}
              square={square}
              onClick={onClick}
            />
          ) : (
            <Fragment key={index}>{item}</Fragment>
          )
        )}
      </div>
      {items.length > 2 && hasOverflow && (
        <div
          className={classNames(
            "bg-gradient-to-l from-white to-transparent w-24 absolute -right-1 top-0 h-full pointer-events-none transition-opacity duration-500",
            {
              "opacity-100": !hasScrolled,
              "opacity-0": hasScrolled
            }
          )}
        >
          <div className="h-[75%] flex items-center">
            <ChevronRight
              className={classNames(
                "w-14 h-14 text-camp-primary animate-scrollRight pointer-events-none transition-opacity duration-500 my-auto",
                {
                  "opacity-100": !hasScrolled,
                  "opacity-0": hasScrolled
                }
              )}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const ListItem = ({
  item,
  square,
  onClick
}: {
  item: Item
  square?: boolean
  onClick?: () => void
}) => (
  <Link
    className={classNames("block", {
      "w-32 lg:w-52": !square,
      "w-32 lg:w-44": square
    })}
    to={item.url}
    handleClick={onClick}
  >
    <span
      className={classNames("block", {
        "w-32 lg:w-52": !square,
        "h-32 w-32 lg:h-44 lg:w-44": square
      })}
    >
      <img
        loading="lazy"
        className="block h-full w-full overflow-hidden rounded-xl object-contain"
        src={item.imageUrl || "/images/collection-placeholder.webp"}
        width={item.imageWidth}
        height={item.imageHeight}
        alt=""
      />
    </span>
    <span className="mt-3 block">
      <h3 className="text-center font-serif">{item.title}</h3>
      {item.region && (
        <p className="mt-2 text-center text-base text-gray-300">
          {item.region}
        </p>
      )}
    </span>
  </Link>
)

export default HorizontalDataList
